/**
 * NotificationEducationScreen
 * Explains the value of push notifications BEFORE requesting permission
 * This is shown during onboarding after the user has defined their mission
 */

import React, { useState } from 'react';
import styles from '../../styles/OnboardingScreen.module.css';

function NotificationEducationScreen({ onNext, onBack, user }) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if notifications are available and current permission status
  const notificationsSupported = 'Notification' in window;
  const notificationPermission = notificationsSupported ? Notification.permission : 'unsupported';
  const canRequestPermission = notificationsSupported && notificationPermission !== 'granted';

  const handleEnableNotifications = async () => {
    if (!isSubmitting && 'Notification' in window) {
      setIsSubmitting(true);

      try {
        // If already granted, just proceed to next
        if (notificationPermission === 'granted') {
          await onNext();
        } else {
          // Request permission
          const permission = await Notification.requestPermission();

          if (permission === 'granted') {
            // Proceed to next screen after granting permission
            await onNext();
          }
        }
      } catch (error) {
        console.error('Error requesting notification permission:', error);
      }

      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (!isSubmitting) {
      setIsSubmitting(true);
      await onNext();
      setIsSubmitting(false);
    }
  };

  // Get user's contact preference for personalization
  const contactTiming = user?.contactInstructions || 'the best times for you';

  // Construct image URL based on environment
  const getImageUrl = (filename) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (apiUrl) {
      return `${apiUrl}/onboarding/${filename}`;
    }
    return `/onboarding/${filename}`;
  };

  return (
    <div className={styles.introContent}>
      {/* Hero image header */}
      <div className={styles.welcomeHeader}>
        <img
          src={getImageUrl('hero-stay-connected.png')}
          alt="Stay Connected to Your Goals"
          className={styles.welcomeHeroImage}
        />
      </div>

      <div className={styles.topSection}>
      </div>

      <div className={styles.introMiddleSection}>
        <div>
          <h1 className={styles.title}>Stay Connected to Your Goals</h1>

          <p className={styles.description}>
            Here's the truth: without reminders, most people forget about their goals and never follow through. Notifications are what keep you on track - I'll reach out {contactTiming}.
          </p>

          <div className={styles.benefitsList}>
            <div className={styles.benefit}>
              <span className={styles.benefitIcon}>📱</span>
              <span>Personalised timing based on your preferences</span>
            </div>
            <div className={styles.benefit}>
              <span className={styles.benefitIcon}>🎯</span>
              <span>Relevant to our conversations, not random spam</span>
            </div>
            <div className={styles.benefit}>
              <span className={styles.benefitIcon}>💪</span>
              <span>The difference between starting and actually finishing</span>
            </div>
          </div>

          <p className={`${styles.note} ${styles.notificationNote}`}>
            You can change notification settings anytime in your device settings.
          </p>
        </div>
      </div>

      <div className={styles.bottomSection}>
        {/* Always show Enable Notifications button if notifications are supported */}
        {notificationsSupported && (
          <button
            className={`${styles.button} ${styles.buttonPrimary} ${styles.installButton}`}
            onClick={handleEnableNotifications}
            disabled={isSubmitting}
          >
            {notificationPermission === 'granted' ? 'Continue (Notifications Enabled ✓)' : 'Enable Notifications'}
          </button>
        )}

        <div className={styles.buttonGroup}>
          {onBack && (
            <button
              className={`${styles.button} ${styles.buttonSecondary}`}
              onClick={onBack}
              disabled={isSubmitting}
            >
              Back
            </button>
          )}
          <button
            className={`${styles.button} ${canRequestPermission ? styles.buttonSecondary : styles.buttonPrimary}`}
            onClick={handleNext}
            disabled={isSubmitting}
          >
            {canRequestPermission ? 'Skip' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NotificationEducationScreen;
